import type { MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';

import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import { logAuthLifecycle, sanitizeMatrixError } from 'modules/Authentication/sessionObservability';
import {
  isAccessTokenNearExpiry,
  PROACTIVE_REFRESH_BUFFER_MS,
  refreshAndApplyMatrixTokens,
} from 'modules/Authentication/utils/matrixTokenRefresh';

import { MatrixLoggingInStatusEnum } from './utils';

/**
 * Browser event name dispatched on `window` after every successful proactive Matrix
 * access-token refresh. Consumers (e.g. `IsTokenProvider`) subscribe to proactively
 * re-exchange their own derived tokens without coupling to this hook.
 */
export const MATRIX_TOKEN_REFRESHED_EVENT = 'matrix:token-refreshed' as const;

/**
 * Capped retry-backoff schedule (ms) used after a transient refresh exhaustion when no
 * `online`/`visibilitychange` event fires (e.g. lid closed for hours on a dead network).
 * Advances one step per consecutive failure and clamps at the last entry (1m → 2m → 5m).
 */
const RECONNECT_RETRY_DELAYS_MS = [60_000, 120_000, 300_000] as const;

/**
 * Proactively refreshes the Matrix access token before cookie expiry so idle tabs
 * do not hit M_UNKNOWN_TOKEN / SessionLoggedOut. Also refreshes when the tab becomes
 * visible again or connectivity returns with an expired or near-expired access token,
 * and resumes the SDK sync loop immediately after a successful refresh.
 */
const useProactiveMatrixTokenRefresh = (
  matrixClient: MatrixClient | null,
  matrixLoggingInStatus: MatrixLoggingInStatusEnum
) => {
  const {
    mxRefreshToken,
    mxAccessTokenExpiresAt,
    mxDeviceId,
    setMxAccessToken,
    setMxRefreshToken,
    setMxAccessTokenExpiresAt,
  } = useMxCookies();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const refreshTokenRef = useRef(mxRefreshToken);
  const expiresAtRef = useRef(mxAccessTokenExpiresAt);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  refreshTokenRef.current = mxRefreshToken;
  expiresAtRef.current = mxAccessTokenExpiresAt;

  const persistTokens = useCallback(
    (tokens: { accessToken: string; refreshToken?: string; accessTokenExpiresAt?: string }) => {
      setMxAccessToken(tokens.accessToken);
      if (tokens.refreshToken) setMxRefreshToken(tokens.refreshToken);
      if (tokens.accessTokenExpiresAt) setMxAccessTokenExpiresAt(tokens.accessTokenExpiresAt);
    },
    [setMxAccessToken, setMxRefreshToken, setMxAccessTokenExpiresAt]
  );

  const runProactiveRefresh = useCallback(async (): Promise<boolean> => {
    if (!matrixClient || !refreshTokenRef.current) return false;
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const telemetry = { phase: 'proactive' as const, mx_device_id: mxDeviceId };
    const promise = refreshAndApplyMatrixTokens(matrixClient, refreshTokenRef.current, persistTokens, telemetry)
      .then(() => {
        logAuthLifecycle('Proactive matrix token refresh succeeded', {
          domain: 'refresh',
          action: 'proactive_refresh',
          outcome: 'success',
          phase: 'proactive',
          mx_device_id: mxDeviceId,
        });
        return true;
      })
      .catch((error) => {
        const sanitized = sanitizeMatrixError(error);
        logAuthLifecycle(
          'Proactive matrix token refresh failed',
          {
            domain: 'refresh',
            action: 'proactive_refresh',
            outcome: sanitized.failure_kind === 'permanent' ? 'permanent_fail' : 'transient_fail',
            phase: 'proactive',
            mx_device_id: mxDeviceId,
          },
          'warn',
          sanitized
        );
        return false;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    refreshInFlightRef.current = promise;
    return promise;
  }, [matrixClient, mxDeviceId, persistTokens]);

  const resetReconnectBackoff = useCallback(() => {
    retryAttemptRef.current = 0;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const handleRefreshSuccess = useCallback(() => {
    // Resume the SDK sync loop immediately instead of waiting for the 2–7s keepalive jitter.
    // Safe no-op (returns false) when the client is not in a keepalive/error state.
    matrixClient?.retryImmediately();
    // Session is healthy again — drop any pending retry-backoff so timers do not stack.
    resetReconnectBackoff();
    // Notify other providers (e.g. IsTokenProvider) that the Matrix access token was
    // refreshed so they can proactively re-exchange their own derived tokens.
    window.dispatchEvent(new Event(MATRIX_TOKEN_REFRESHED_EVENT));
  }, [matrixClient, resetReconnectBackoff]);

  // Ref indirection lets the backoff timer re-enter the refresh cycle without a useCallback dependency cycle.
  const runRefreshCycleRef = useRef<() => Promise<void>>();

  const scheduleReconnectRetry = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    const delay = RECONNECT_RETRY_DELAYS_MS[Math.min(retryAttemptRef.current, RECONNECT_RETRY_DELAYS_MS.length - 1)];
    retryAttemptRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      void runRefreshCycleRef.current?.();
    }, delay);
  }, []);

  // Shared cycle for every refresh trigger (proactive timer, visibilitychange, online, retry timer):
  // route through runProactiveRefresh (single in-flight guard), resume sync once on success, and
  // schedule a capped retry-backoff on failure so a long offline gap still self-heals.
  const runRefreshCycle = useCallback(async () => {
    const success = await runProactiveRefresh();
    if (success) {
      handleRefreshSuccess();
    } else {
      scheduleReconnectRetry();
    }
  }, [runProactiveRefresh, handleRefreshSuccess, scheduleReconnectRetry]);

  runRefreshCycleRef.current = runRefreshCycle;

  const scheduleProactiveRefresh = useCallback(() => {
    clearTimeout(timerRef.current);

    const expiresAtIso = expiresAtRef.current;
    const refreshToken = refreshTokenRef.current;
    if (!matrixClient || !refreshToken || !expiresAtIso) return;

    const refreshAt = new Date(expiresAtIso).getTime() - PROACTIVE_REFRESH_BUFFER_MS;
    const delay = Math.max(0, refreshAt - Date.now());

    timerRef.current = setTimeout(() => {
      void runRefreshCycle();
    }, delay);
  }, [matrixClient, runRefreshCycle]);

  useEffect(() => {
    if (!matrixClient || matrixLoggingInStatus !== MatrixLoggingInStatusEnum.FINISHED || !mxRefreshToken) {
      return;
    }

    scheduleProactiveRefresh();

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (isAccessTokenNearExpiry(expiresAtRef.current)) {
        // Fresh user-driven attempt supersedes any pending backoff so timers do not stack.
        resetReconnectBackoff();
        void runRefreshCycle();
      }
    };

    const onOnline = () => {
      if (isAccessTokenNearExpiry(expiresAtRef.current)) {
        resetReconnectBackoff();
        void runRefreshCycle();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    return () => {
      clearTimeout(timerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [
    matrixClient,
    matrixLoggingInStatus,
    mxRefreshToken,
    mxAccessTokenExpiresAt,
    scheduleProactiveRefresh,
    runRefreshCycle,
    resetReconnectBackoff,
  ]);
};

export default useProactiveMatrixTokenRefresh;
